/**
 * Per-storey airflow requirements table (the ASHRAE trust pattern, mirroring
 * the findings panel style): space, occupancy, design cfm, the screening
 * method that produced it, and a section-number citation chip (e.g.
 * "ASHRAE 62.1 §6.2.2.1" — never code prose). Methods are screening-level
 * (ventilation-driven), and the caption says so.
 */
import type { CSSProperties } from "react";
import type { ApiSpace, MepAirflow } from "../api/types";
import { citationText } from "./FindingsPanel";

export interface AirflowRow {
  airflow: MepAirflow;
  /** Joined space (null when the space id is unknown to the model). */
  space: ApiSpace | null;
}

/**
 * Join airflows with their spaces and sort by space name then space id —
 * deterministic for identical inputs.
 */
export function airflowRows(
  airflows: readonly MepAirflow[],
  spaces: readonly ApiSpace[],
): AirflowRow[] {
  const byId = new Map(spaces.map((s) => [s.id, s]));
  return airflows
    .map((airflow) => ({ airflow, space: byId.get(airflow.space_id) ?? null }))
    .sort((a, b) => {
      const an = a.space?.name ?? "";
      const bn = b.space?.name ?? "";
      if (an !== bn) return an < bn ? -1 : 1;
      return a.airflow.space_id < b.airflow.space_id ? -1 : a.airflow.space_id > b.airflow.space_id ? 1 : 0;
    });
}

const chipStyle: CSSProperties = {
  display: "inline-block",
  padding: "0.05rem 0.45rem",
  borderRadius: 999,
  fontSize: "0.72rem",
  background: "#2a2a44",
  color: "#8e8eb8",
  border: "1px solid #555577",
  whiteSpace: "nowrap",
};

const headerCell: CSSProperties = {
  textAlign: "left",
  padding: "0.3rem 0.6rem 0.3rem 0",
  color: "#8e8eb8",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const cell: CSSProperties = {
  padding: "0.3rem 0.6rem 0.3rem 0",
  fontSize: "0.85rem",
  verticalAlign: "top",
};

export interface AirflowTableProps {
  /** Airflows already filtered to the visible storey. */
  airflows: MepAirflow[];
  /** Spaces of the model (joined by space id for name/occupancy). */
  spaces: ApiSpace[];
}

export function AirflowTable({ airflows, spaces }: AirflowTableProps) {
  const rows = airflowRows(airflows, spaces);

  if (rows.length === 0) {
    return (
      <p data-testid="airflow-empty" style={{ color: "#8e8eb8" }}>
        No airflow requirements on this storey.
      </p>
    );
  }

  return (
    <section data-testid="airflow-table">
      <p style={{ margin: "0 0 0.4rem", color: "#8e8eb8", fontSize: "0.78rem" }}>
        Screening-level airflow requirements (ventilation-driven) — every value carries its method
        and rule citation.
      </p>
      <table style={{ width: "100%", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th style={headerCell}>Space</th>
            <th style={headerCell}>Occupancy</th>
            <th style={{ ...headerCell, textAlign: "right" }}>cfm</th>
            <th style={headerCell}>Method</th>
            <th style={headerCell}>Citation</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ airflow, space }) => (
            <tr
              key={airflow.space_id}
              data-testid="airflow-row"
              data-space-id={airflow.space_id}
              style={{ borderTop: "1px solid #2a2a44" }}
            >
              <td style={cell}>{space?.name ?? airflow.space_id}</td>
              <td style={{ ...cell, color: "#8e8eb8" }}>
                {space?.effective_occupancy ??
                  space?.occupancy_override ??
                  space?.occupancy ??
                  "unclassified"}
              </td>
              <td style={{ ...cell, textAlign: "right", fontFamily: "monospace" }}>
                {Math.round(airflow.cfm).toLocaleString()}
              </td>
              <td style={{ ...cell, color: "#8e8eb8" }}>{airflow.method.replace(/_/g, " ")}</td>
              <td style={cell}>
                {airflow.citation ? (
                  <span data-testid="airflow-citation" style={chipStyle}>
                    {citationText(airflow.citation)}
                  </span>
                ) : (
                  <span style={{ color: "#555577" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
